import {
  Box, Typography, Grid, TextField, Button, MenuItem, Avatar,
  Snackbar, Alert, Chip, Divider,
} from "@mui/material";
import React, { useState } from "react";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import { LoansApi } from "../../api/requests/Loans";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const loanTypes = [
  { value: "personal", label: "Personal Loan", icon: <PersonIcon />, rate: "10.5% - 18%", maxAmt: "Rs. 25,00,000" },
  { value: "business", label: "Business Loan", icon: <BusinessCenterIcon />, rate: "12% - 20%", maxAmt: "Rs. 50,00,000" },
  { value: "home", label: "Home Loan", icon: <HomeWorkIcon />, rate: "8.5% - 12%", maxAmt: "Rs. 1,00,00,000" },
  { value: "other_loan", label: "Other Loan"},
  { value: "ca_registration", label: "CA Registration"},
  { value: "hospital_cash", label: "Hospital Cash"},
  { value: "business_loan", label: "Business Loan"},
];

const Loans = () => {
  const [formData, setFormData] = useState({
    loan_type: "",
    full_name: "",
    email: "",
    phone: "",
    pan_number: "",
    monthly_income: "",
    loan_amount: "",
    employment_type: "",
    purpose: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.loan_type || !formData.full_name || !formData.phone || !formData.loan_amount) {
      setSnackbar({ open: true, message: "Please fill all required fields", severity: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await LoansApi.submitLoanApplication(formData);
      if (response?.message) {
        setSubmitted(true);
        setSnackbar({
          open: true,
          message: response.message,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Something went wrong. Please try again.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to submit loan application. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLoan = loanTypes.find(l => l.value === formData.loan_type);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: "14px",
      "& fieldset": { borderColor: Colors.dividerColor },
      "&:hover fieldset": { borderColor: Colors.primary },
      "&.Mui-focused fieldset": { borderColor: Colors.primary, borderWidth: "2px" },
    },
    "& .MuiInputLabel-root": { fontSize: "13px", color: Colors.secondaryColor },
    "& .MuiInputLabel-root.Mui-focused": { color: Colors.primary },
  };

  if (submitted) {
    return (
      <Box sx={{
        width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
        ml: { md: `${SideBarWidth}px` },
        minHeight: "100vh", backgroundColor: Colors.bgColor,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Box sx={{ textAlign: "center", px: 3, maxWidth: 480 }}>
          <Avatar sx={{ width: 80, height: 80, mx: "auto", mb: 3, backgroundColor: Colors.greenSoft, color: Colors.insuranceGreen }}>
            <CheckCircleIcon sx={{ fontSize: 44 }} />
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: "24px", color: Colors.primaryTextColor, mb: 1 }}>
            Application Submitted!
          </Typography>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "15px", lineHeight: 1.7, mb: 3 }}>
            Your {selectedLoan?.label || "loan"} application for Rs. {formData.loan_amount} has been submitted. Our team will review and contact you within 24-48 hours.
          </Typography>
          <Button
            variant="contained"
            onClick={() => { setSubmitted(false); setFormData({ loan_type: "", full_name: "", email: "", phone: "", pan_number: "", monthly_income: "", loan_amount: "", employment_type: "", purpose: "" }); }}
            sx={{
              backgroundColor: Colors.primary, fontWeight: 600, fontSize: "14px",
              borderRadius: "10px", px: 4, py: 1.2, textTransform: "none", boxShadow: "none",
              "&:hover": { backgroundColor: "#1D4ED8", boxShadow: "0 4px 12px rgba(37,99,235,0.25)" },
            }}
          >
            Apply for Another Loan
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` },
      minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, maxWidth: 1000 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <RequestQuoteIcon sx={{ fontSize: 22, color: "#F59E0B" }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Loan Application
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "13px" }}>
            Fill in your details to apply for a loan. Our team will process your application.
          </Typography>
        </Box>

        {/* Loan Type Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {loanTypes.map((loan) => (
            <Grid item xs={12} sm={4} key={loan.value}>
              <Box
                onClick={() => setFormData({ ...formData, loan_type: loan.value })}
                sx={{
                  p: 2.5, borderRadius: "12px", backgroundColor: Colors.white,
                  border: `2px solid ${formData.loan_type === loan.value ? "#F59E0B" : Colors.dividerColor}`,
                  cursor: "pointer", transition: "all 0.2s",
                  "&:hover": { borderColor: "#F59E0B", boxShadow: "0 4px 16px rgba(245,158,11,0.1)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Avatar sx={{
                    width: 40, height: 40,
                    backgroundColor: formData.loan_type === loan.value ? "#FFFBEB" : Colors.bgColor,
                    color: formData.loan_type === loan.value ? "#F59E0B" : Colors.secondaryColor,
                  }}>
                    {loan.icon}
                  </Avatar>
                  <Typography sx={{ fontWeight: 600, fontSize: "14px", color: Colors.primaryTextColor }}>
                    {loan.label}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>Interest</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: Colors.primaryTextColor }}>{loan.rate}</Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, color: Colors.secondaryColor, textTransform: "uppercase", letterSpacing: "0.5px" }}>Max Amount</Typography>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: Colors.primaryTextColor }}>{loan.maxAmt}</Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Application Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            backgroundColor: Colors.white, borderRadius: "12px",
            border: `1px solid ${Colors.dividerColor}`, overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, py: 2.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "16px", color: Colors.primaryTextColor }}>
              Applicant Details
            </Typography>
            <Typography sx={{ fontSize: "13px", color: Colors.secondaryColor, mt: 0.3 }}>
              Fields marked with * are required
            </Typography>
          </Box>
          <Divider sx={{ borderColor: Colors.dividerColor }} />

          <Box sx={{ p: 3 }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name *" name="full_name" value={formData.full_name} onChange={handleChange} size="small" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} size="small" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone Number *" name="phone" value={formData.phone} onChange={handleChange} size="small" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PAN Number" name="pan_number" value={formData.pan_number} onChange={handleChange} size="small" sx={fieldSx}
                  inputProps={{ style: { textTransform: "uppercase" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Monthly Income (Rs.)" name="monthly_income" type="number" value={formData.monthly_income} onChange={handleChange} size="small" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Loan Amount Required (Rs.) *" name="loan_amount" type="number" value={formData.loan_amount} onChange={handleChange} size="small" sx={fieldSx} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Employment Type" name="employment_type" value={formData.employment_type} onChange={handleChange} size="small" sx={fieldSx}>
                  <MenuItem value="salaried">Salaried</MenuItem>
                  <MenuItem value="self_employed">Self Employed</MenuItem>
                  <MenuItem value="business">Business Owner</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth select label="Loan Type *" name="loan_type" value={formData.loan_type} onChange={handleChange} size="small" sx={fieldSx}>
                  {loanTypes.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Purpose of Loan" name="purpose" value={formData.purpose} onChange={handleChange} size="small" multiline rows={3} sx={fieldSx} />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setFormData({ loan_type: "", full_name: "", email: "", phone: "", pan_number: "", monthly_income: "", loan_amount: "", employment_type: "", purpose: "" })}
                sx={{
                  borderColor: Colors.dividerColor, color: Colors.secondaryColor, fontWeight: 600,
                  fontSize: "13px", borderRadius: "10px", px: 3, textTransform: "none",
                }}
              >
                Clear
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{
                  backgroundColor: "#F59E0B", fontWeight: 600, fontSize: "14px",
                  borderRadius: "10px", px: 4, py: 1, textTransform: "none", boxShadow: "none",
                  "&:hover": { backgroundColor: "#D97706", boxShadow: "0 4px 12px rgba(245,158,11,0.3)" },
                }}
              >
                {submitting ? "Submitting..." : "Submit Application"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: "10px" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Loans;
